/**
 * Risk calculation thresholds for edit metrics (AI detection).
 *
 * IMPORTANT: These thresholds are also defined in Python:
 *   apps/api/src/markeroo_api/services/extension.py (RISK_THRESHOLDS)
 *
 * A test in packages/python/markeroo_shared/tests/test_edit_metrics_sync.py
 * verifies that both definitions stay in sync.
 *
 * If you change these values, update the Python version too!
 */

// Note: RiskLevel type is now defined in schemas/index.ts

/**
 * Constants for edit metrics risk calculation and warning thresholds.
 * Used by frontend components and mirrored in Python backend.
 */
export const EDIT_METRICS_THRESHOLDS = {
  // Paste ratio thresholds
  PASTE_RATIO_VERY_HIGH: 0.8, // Very suspicious
  PASTE_RATIO_HIGH: 0.5, // Suspicious
  PASTE_RATIO_MODERATE: 0.3, // Slightly elevated

  // Error correction rate thresholds (natural typing is ~0.2-0.4)
  ERROR_CORRECTION_VERY_LOW: 0.05, // Very suspicious
  ERROR_CORRECTION_LOW: 0.1, // Suspicious
  ERROR_CORRECTION_MIN_INSERTS: 50, // Minimum inserts to consider error correction meaningful

  // WPM thresholds (normal typing is 30-80 WPM)
  WPM_VERY_HIGH: 200, // Very suspicious
  WPM_HIGH: 150, // Suspicious

  // Typing variance thresholds (Coefficient of Variation = std_dev / avg_interval_ms)
  // Normal human typing has CV around 0.3-0.6
  // AI-generated content shows unnaturally low CV (< 0.2)
  TYPING_VARIANCE_CV_VERY_LOW: 0.15, // Very suspicious - almost mechanical
  TYPING_VARIANCE_CV_LOW: 0.25, // Suspicious - too consistent
  TYPING_VARIANCE_MIN_KEYSTROKES: 20, // Minimum keystrokes for meaningful analysis

  // Events per word thresholds
  EVENTS_PER_WORD_VERY_LOW: 2, // Very few edits
  EVENTS_PER_WORD_LOW: 4, // Few edits
  MIN_WORD_COUNT_FOR_EVENTS_CHECK: 100, // Minimum words to consider events-per-word

  // Paste size thresholds (characters)
  PASTE_SIZE_VERY_LARGE: 500,
  PASTE_SIZE_LARGE: 200,

  // Risk score thresholds
  RISK_SCORE_HIGH: 5,
  RISK_SCORE_MEDIUM: 2,
} as const;

export type EditMetricsThresholds = typeof EDIT_METRICS_THRESHOLDS;
